/**
 * Sizing of the downscaled WebP copies of stored pictures (migration 0033, docs/dev/api-documents.md „Image
 * variants"). The server renders them (`file.variants`); the client builds `srcset` candidates from the same plan so
 * that every candidate width is the real width of the bytes behind it.
 *
 * Never upscaled: the variants are made in ascending order down to the first one that already holds the full size —
 * a 1000 px picture gets `thumb` (400 px) and `medium` (1000 px) only, and `?variant=large` answers with `medium`.
 */
export const FILE_VARIANT_KINDS = ['thumb', 'medium', 'large'];
/** Longest edge of each variant in pixels. */
export const VARIANT_LONG_EDGE = { thumb: 400, medium: 1200, large: 2400 };
/** Scales (width, height) so that the longer edge is at most `longEdge`; never enlarges. */
export function fitWithin(width, height, longEdge) {
    const longest = Math.max(width, height);
    if (longest <= longEdge)
        return { width, height };
    const scale = longEdge / longest;
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
}
/** The variants of a picture of the given (EXIF-oriented) size, up to and including the first full-size one. */
export function planVariants(width, height) {
    const planned = [];
    for (const variant of FILE_VARIANT_KINDS) {
        const size = fitWithin(width, height, VARIANT_LONG_EDGE[variant]);
        planned.push({ variant, ...size });
        if (size.width === width && size.height === height)
            break;
    }
    return planned;
}
/**
 * The variant served for a request: the requested one when it exists, else the largest existing smaller one (the
 * full-size copy when the set is complete); null when none exists yet and the original is served.
 */
export function resolveVariant(requested, available) {
    for (let at = FILE_VARIANT_KINDS.indexOf(requested); at >= 0; at -= 1) {
        const candidate = FILE_VARIANT_KINDS[at];
        if (candidate !== undefined && available.has(candidate))
            return candidate;
    }
    return null;
}
/** `/api/files/<id>?variant=<kind>` */
export function fileVariantUrl(fileId, variant) {
    return `/api/files/${fileId}?variant=${variant}`;
}
/**
 * `srcset` of a stored picture: one candidate per planned variant with its real width. Without a known size (the
 * variants are not rendered yet) the nominal widths are used — the server answers with the original meanwhile.
 * `upTo` drops the larger variants (the gallery grid never needs more than `medium`).
 */
export function fileVariantSrcSet(fileId, size, upTo = 'large') {
    const limit = FILE_VARIANT_KINDS.indexOf(upTo);
    const planned = size.width !== null && size.height !== null && size.width > 0 && size.height > 0
        ? planVariants(size.width, size.height)
        : FILE_VARIANT_KINDS.map((variant) => ({ variant, width: VARIANT_LONG_EDGE[variant], height: 0 }));
    return planned
        .filter((entry) => FILE_VARIANT_KINDS.indexOf(entry.variant) <= limit)
        .map((entry) => `${fileVariantUrl(fileId, entry.variant)} ${entry.width}w`)
        .join(', ');
}
//# sourceMappingURL=image-variants.js.map